import dagre from '@dagrejs/dagre';
import { planLayout } from './layout.js';
import { runbookDescendants, runbookExecutionOrder, runbookHierarchy } from './runbook-graph.js';

const DEFAULT_COLOR = '#64748b';
const MIN_NODE_GAP = 64;
const MIN_RANK_GAP = 96;
const PHASE_BRANCH_GAP = 152;
const DEFAULT_SPACING = Object.freeze({ node: 60, rank: 110 });

function dimensions(node) {
    return {
        width: Number(node?.measured?.width ?? node?.width ?? 230),
        height: Number(node?.measured?.height ?? node?.height ?? 120),
    };
}

function depthOf(node) {
    return Math.max(0, String(node?.data?.stepNumber ?? '').split('.').length - 1);
}

export function buildRunbookPhaseFrames(nodes, edges, padding = 28) {
    const visible = new Map((nodes ?? []).filter((node) => !node.hidden).map((node) => [node.id, node]));
    const { children, parent } = runbookHierarchy(edges);
    const roots = [...children.keys()].filter((id) => visible.has(id));

    return roots.map((rootId) => {
        const root = visible.get(rootId);
        const memberIds = [rootId, ...runbookDescendants(rootId, children)].filter((id) => visible.has(id));
        const members = memberIds.map((id) => visible.get(id));
        const left = Math.min(...members.map((node) => Number(node.position?.x ?? 0)));
        const top = Math.min(...members.map((node) => Number(node.position?.y ?? 0)));
        const right = Math.max(...members.map((node) => Number(node.position?.x ?? 0) + dimensions(node).width));
        const bottom = Math.max(...members.map((node) => Number(node.position?.y ?? 0) + dimensions(node).height));
        let depth = 0;
        let current = rootId;
        while (parent.has(current)) {
            depth++;
            current = parent.get(current);
        }

        return {
            id: rootId,
            label: root.data?.title ?? 'Phase',
            color: root.data?.phaseColor ?? DEFAULT_COLOR,
            collapsed: root.data?.runbookCollapsed === true,
            visibleChildren: Math.max(0, memberIds.length - 1),
            depth,
            x: left - padding,
            y: top - padding - 12,
            width: right - left + padding * 2,
            height: bottom - top + padding * 2 + 12,
        };
    });
}

export function layoutRunbookLine(nodes, direction = 'TB', gap = 90, indent = 44) {
    const visible = runbookExecutionOrder((nodes ?? []).filter((node) => !node.hidden));
    const positions = new Map();
    let cursor = 0;

    for (const node of visible) {
        const size = dimensions(node);
        const depth = depthOf(node);
        const horizontal = direction === 'LR' || direction === 'RL';
        positions.set(node.id, horizontal
            ? { x: direction === 'RL' ? -cursor - size.width : cursor, y: depth * indent }
            : { x: depth * indent, y: cursor });
        cursor += (horizontal ? size.width : size.height) + gap;
    }

    return (nodes ?? []).map((node) => positions.has(node.id)
        ? { ...node, position: positions.get(node.id) }
        : node);
}

export function layoutRunbookBranch(
    nodes,
    edges,
    focusNodeId,
    direction = 'TB',
    spacing = null,
    style = 'hierarchy',
) {
    const resolvedSpacing = spacing ?? DEFAULT_SPACING;
    const visible = new Map((nodes ?? []).filter((node) => !node.hidden).map((node) => [node.id, node]));
    const { children } = runbookHierarchy(edges);
    const ids = [focusNodeId, ...runbookDescendants(focusNodeId, children)]
        .filter((id) => visible.has(id));
    const branch = ids.map((id) => visible.get(id));
    const focus = visible.get(focusNodeId);
    if (!focus || branch.length < 2) return nodes;

    let laid;
    if (style === 'linear') {
        laid = layoutRunbookLine(branch, direction === 'BT' ? 'TB' : direction, resolvedSpacing.rank);
    } else {
        const graph = new dagre.graphlib.Graph();
        const layoutSpacing = runbookHierarchySpacing(direction, resolvedSpacing);
        graph.setDefaultEdgeLabel(() => ({}));
        graph.setGraph({ rankdir: direction, ...layoutSpacing, marginx: 0, marginy: 0 });
        for (const node of branch) graph.setNode(node.id, dimensions(node));
        const branchIds = new Set(ids);
        const internal = (edges ?? []).filter((edge) => !edge.hidden
            && branchIds.has(edge.source) && branchIds.has(edge.target));
        for (const edge of planLayout(internal).ranked) {
            graph.setEdge(edge.source, edge.target, { weight: edge.weight });
        }
        dagre.layout(graph);
        laid = branch.map((node) => {
            const position = graph.node(node.id);

            return {
                ...node,
                position: {
                    x: position.x - position.width / 2,
                    y: position.y - position.height / 2,
                },
            };
        });
    }

    const laidFocus = laid.find((node) => node.id === focusNodeId);
    const offset = {
        x: focus.position.x - laidFocus.position.x,
        y: focus.position.y - laidFocus.position.y,
    };
    const positions = new Map(laid.map((node) => [node.id, {
        x: node.position.x + offset.x,
        y: node.position.y + offset.y,
    }]));

    return (nodes ?? []).map((node) => positions.has(node.id)
        ? { ...node, position: positions.get(node.id) }
        : node);
}

export function runbookHierarchySpacing(direction, spacing) {
    const horizontal = direction === 'LR' || direction === 'RL';

    return {
        nodesep: Math.max(MIN_NODE_GAP, horizontal
            ? Math.round(Number(spacing?.node ?? 60) * 0.6)
            : Number(spacing?.node ?? 60)),
        ranksep: Math.max(MIN_RANK_GAP, horizontal
            ? Math.round(Number(spacing?.rank ?? 110) * 1.5)
            : Number(spacing?.rank ?? 110)),
    };
}

function phaseFrame(members) {
    const left = Math.min(...members.map((node) => node.position.x));
    const top = Math.min(...members.map((node) => node.position.y));
    const right = Math.max(...members.map((node) => node.position.x + dimensions(node).width));
    const bottom = Math.max(...members.map((node) => node.position.y + dimensions(node).height));

    return { left: left - 28, right: right + 28, top: top - 40, bottom: bottom + 28 };
}

function phaseGroup(root, ids, members) {
    const frame = phaseFrame(members);
    const size = dimensions(root);
    const anchor = {
        x: root.position.x + size.width / 2,
        y: root.position.y + size.height / 2,
    };

    return {
        id: root.id,
        ids,
        anchor,
        width: Math.max(anchor.x - frame.left, frame.right - anchor.x) * 2,
        height: Math.max(anchor.y - frame.top, frame.bottom - anchor.y) * 2,
    };
}

function standaloneGroup(node) {
    const size = dimensions(node);

    return {
        id: node.id,
        ids: [node.id],
        anchor: {
            x: node.position.x + size.width / 2,
            y: node.position.y + size.height / 2,
        },
        width: size.width,
        height: size.height,
    };
}

function phaseGroups(nodes, edges) {
    const visible = (nodes ?? []).filter((node) => !node.hidden);
    const byId = new Map(visible.map((node) => [node.id, node]));
    const { children, parent } = runbookHierarchy(edges);
    const roots = runbookExecutionOrder(
        visible.filter((node) => !node.data?.marker && !parent.has(node.id)
            && String(node.data?.stepNumber ?? '').split('.').length === 1),
    );
    const owner = new Map();
    const groups = [];

    for (const root of roots) {
        const branchIds = [root.id, ...runbookDescendants(root.id, children)];
        const ids = branchIds.filter((id) => byId.has(id));
        for (const id of branchIds) owner.set(id, root.id);
        groups.push({ root, ids });
    }

    for (const node of visible.filter((item) => !owner.has(item.id))) {
        owner.set(node.id, node.id);
        groups.push({ root: node, ids: [node.id] });
    }

    return { groups, owner };
}

function shiftNodes(nodes, ids, x, y) {
    const moving = new Set(ids);

    return nodes.map((node) => moving.has(node.id)
        ? { ...node, position: { x: node.position.x + x, y: node.position.y + y } }
        : node);
}

function groupFrame(nodesById, ids) {
    const members = ids.map((id) => nodesById.get(id)).filter(Boolean);

    if (members.length === 1 && members[0].data?.marker) {
        const node = members[0];
        const size = dimensions(node);

        return {
            left: node.position.x,
            right: node.position.x + size.width,
            top: node.position.y,
            bottom: node.position.y + size.height,
        };
    }

    return phaseFrame(members);
}

function requiredPrimaryShift(sources, target, direction, gap) {
    if (direction === 'RL') {
        return Math.min(...sources.map((source) => source.left - gap)) - target.right;
    }
    if (direction === 'TB') {
        return Math.max(...sources.map((source) => source.bottom + gap)) - target.top;
    }
    if (direction === 'BT') {
        return Math.min(...sources.map((source) => source.top - gap)) - target.bottom;
    }

    return Math.max(...sources.map((source) => source.right + gap)) - target.left;
}

function primaryShift(amount, direction) {
    if (direction === 'RL') return { x: -amount, y: 0 };
    if (direction === 'TB') return { x: 0, y: amount };
    if (direction === 'BT') return { x: 0, y: -amount };

    return { x: amount, y: 0 };
}

function restoreGroupAnchors(beforeById, laidById, laid, groups, focusOwner, focusNodeId) {
    let result = laid;
    for (const group of groups) {
        const anchorId = group.root.id === focusOwner ? focusNodeId : group.root.id;
        const previous = beforeById.get(anchorId);
        const current = laidById.get(anchorId);
        if (!previous || !current) continue;
        result = shiftNodes(
            result,
            group.ids,
            previous.position.x - current.position.x,
            previous.position.y - current.position.y,
        );
    }

    return result;
}

function phaseConnections(edges, owner) {
    const outgoing = new Map();
    const incoming = new Map();
    const connections = (edges ?? []).filter((edge) => !edge.hidden)
        .map((edge) => ({ source: owner.get(edge.source), target: owner.get(edge.target) }))
        .filter((edge) => edge.source && edge.target && edge.source !== edge.target);

    for (const edge of connections) {
        if (!outgoing.has(edge.source)) outgoing.set(edge.source, []);
        outgoing.get(edge.source).push(edge.target);
        if (!incoming.has(edge.target)) incoming.set(edge.target, []);
        incoming.get(edge.target).push(edge.source);
    }

    return { outgoing, incoming };
}

function reachableFrom(sourceId, outgoing) {
    const found = new Set();
    const pending = [...(outgoing.get(sourceId) ?? [])];
    while (pending.length) {
        const id = pending.shift();
        if (!id || found.has(id)) continue;
        found.add(id);
        pending.push(...(outgoing.get(id) ?? []));
    }

    return found;
}

function settleDownstream(nodes, groups, downstream, incoming, direction, gap) {
    const groupById = new Map(groups.map((group) => [group.root.id, group]));
    let result = nodes;
    let passesRemaining = groups.length;
    let moved = true;

    while (moved && passesRemaining > 0) {
        moved = false;
        passesRemaining--;
        for (const targetId of downstream) {
            const targetGroup = groupById.get(targetId);
            const sourceGroups = (incoming.get(targetId) ?? [])
                .map((id) => groupById.get(id))
                .filter(Boolean);
            if (!targetGroup || sourceGroups.length === 0) continue;
            const current = new Map(result.map((node) => [node.id, node]));
            const amount = requiredPrimaryShift(
                sourceGroups.map((group) => groupFrame(current, group.ids)),
                groupFrame(current, targetGroup.ids),
                direction,
                gap,
            );
            if (Math.abs(amount) < 0.5) continue;
            const delta = primaryShift(amount, direction);
            result = shiftNodes(result, targetGroup.ids, delta.x, delta.y);
            moved = true;
        }
    }

    return result;
}

function primaryBandsOverlap(left, right, direction) {
    return direction === 'TB' || direction === 'BT'
        ? left.top < right.bottom && left.bottom > right.top
        : left.left < right.right && left.right > right.left;
}

function crossAxis(frame, direction) {
    return direction === 'TB' || direction === 'BT'
        ? { start: frame.left, end: frame.right }
        : { start: frame.top, end: frame.bottom };
}

function crossShift(amount, direction) {
    return direction === 'TB' || direction === 'BT'
        ? { x: amount, y: 0 }
        : { x: 0, y: amount };
}

function packParallelPeers(nodes, groups, focusOwner, downstream, direction, gap) {
    const groupById = new Map(groups.map((group) => [group.root.id, group]));
    const focusGroup = groupById.get(focusOwner);
    if (!focusGroup) return nodes;

    let result = nodes;
    const frames = new Map();
    const current = new Map(result.map((node) => [node.id, node]));
    for (const group of groups) frames.set(group.root.id, groupFrame(current, group.ids));
    const focusFrame = frames.get(focusOwner);
    const focusCross = crossAxis(focusFrame, direction);
    const peers = groups.filter((group) => group.root.id !== focusOwner
        && !downstream.has(group.root.id)
        && primaryBandsOverlap(focusFrame, frames.get(group.root.id), direction));
    const before = peers.filter((group) => {
        const cross = crossAxis(frames.get(group.root.id), direction);

        return (cross.start + cross.end) / 2 < (focusCross.start + focusCross.end) / 2;
    }).sort((left, right) => crossAxis(frames.get(right.root.id), direction).end
        - crossAxis(frames.get(left.root.id), direction).end);
    const after = peers.filter((group) => !before.includes(group))
        .sort((left, right) => crossAxis(frames.get(left.root.id), direction).start
            - crossAxis(frames.get(right.root.id), direction).start);

    let boundary = focusCross.start - gap;
    for (const group of before) {
        const cross = crossAxis(frames.get(group.root.id), direction);
        const amount = Math.min(0, boundary - cross.end);
        const delta = crossShift(amount, direction);
        result = shiftNodes(result, group.ids, delta.x, delta.y);
        boundary = cross.start + amount - gap;
    }

    boundary = focusCross.end + gap;
    for (const group of after) {
        const cross = crossAxis(frames.get(group.root.id), direction);
        const amount = Math.max(0, boundary - cross.start);
        const delta = crossShift(amount, direction);
        result = shiftNodes(result, group.ids, delta.x, delta.y);
        boundary = cross.end + amount + gap;
    }

    return result;
}

/**
 * Slide whatever a just-expanded branch now covers out of its way — and nothing else.
 *
 * The middle ground the flow canvas settled on after trying both extremes. Auto-tidy on
 * every toggle rearranged the whole drawing and made each click a surprise; moving nothing
 * at all opened a branch's children straight over the collapsed groups beside it. So: any
 * group actually intersecting the expanded branch is pushed along the flow's primary axis
 * exactly far enough to clear it, groups that group then hits are pushed the same way, and
 * a group not in the way never moves. A translation, not a layout — each pushed group keeps
 * its internal arrangement, and the drawing keeps its direction.
 */
export function makeRoomForBranch(nodes, edges, focusNodeId, direction = 'TB', gap = PHASE_BRANCH_GAP) {
    const { groups, owner } = phaseGroups(nodes, edges);
    const focusOwner = owner.get(focusNodeId) ?? focusNodeId;
    if (!groups.some((group) => group.root.id === focusOwner)) return { nodes, displaced: [] };

    let result = nodes;
    const frameFor = (group) => {
        const visible = new Map(result.filter((item) => !item.hidden).map((item) => [item.id, item]));
        const ids = group.ids.filter((id) => visible.has(id));

        return ids.length ? groupFrame(visible, ids) : null;
    };
    const intersects = (a, b) =>
        Math.min(a.right, b.right) - Math.max(a.left, b.left) > 8
        && Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) > 8;
    const clearance = (pusher, frame) => {
        if (direction === 'LR') return { x: pusher.right + gap - frame.left, y: 0 };
        if (direction === 'RL') return { x: pusher.left - gap - frame.right, y: 0 };
        if (direction === 'BT') return { x: 0, y: pusher.top - gap - frame.bottom };

        return { x: 0, y: pusher.bottom + gap - frame.top };
    };

    /*
     * A group may be pushed more than once: three groups standing in the same band are each
     * cleared of the branch, land beside one another, and must then be cleared of each other.
     * The first cut of this marked a group "moved" and never touched it again, which resolved
     * branch-versus-group and quietly left group-versus-group — measured on the same flow that
     * motivated all of this: the branch clean, and Security, Network and Database stacked.
     * Termination is by direction, not by the once-only rule: every push moves the same way
     * along one axis, so nothing can oscillate; the guard is a backstop, not the mechanism.
     */
    const queue = [focusOwner];
    let guard = groups.length * groups.length + 16;
    // Everything this expansion moves, and by how much in total — so collapsing the same
    // branch can slide each group back and the connections shorten again. Deltas, not
    // positions: an absolute snapshot is what once reverted a re-tidied vertical flow to
    // horizontal, and this must stay immune to that by construction.
    const displacedByRoot = new Map();

    while (queue.length && guard-- > 0) {
        const pusherId = queue.shift();
        const pusherGroup = groups.find((group) => group.root.id === pusherId);
        const pushFrame = pusherGroup ? frameFor(pusherGroup) : null;
        if (!pushFrame) continue;

        for (const group of groups) {
            if (group.root.id === pusherId || group.root.id === focusOwner) continue;
            const frame = frameFor(group);
            if (!frame || !intersects(pushFrame, frame)) continue;

            const delta = clearance(pushFrame, frame);
            // Hidden members shift with their group, so a later expansion opens coherently.
            result = shiftNodes(result, group.ids, delta.x, delta.y);
            const record = displacedByRoot.get(group.root.id)
                ?? { ids: [...group.ids], dx: 0, dy: 0 };
            record.dx += delta.x;
            record.dy += delta.y;
            displacedByRoot.set(group.root.id, record);
            queue.push(group.root.id);
        }
    }

    return { nodes: result, displaced: [...displacedByRoot.values()] };
}

export function preserveRunbookExpansionContext(before, laid, edges, focusNodeId, direction = 'TB', gap = PHASE_BRANCH_GAP) {
    const beforeById = new Map((before ?? []).map((node) => [node.id, node]));
    const laidById = new Map((laid ?? []).map((node) => [node.id, node]));
    const { groups, owner } = phaseGroups(laid, edges);
    const focusOwner = owner.get(focusNodeId);
    if (!focusOwner || !beforeById.has(focusNodeId)) return laid;

    const restored = restoreGroupAnchors(
        beforeById,
        laidById,
        laid,
        groups,
        focusOwner,
        focusNodeId,
    );
    const { outgoing, incoming } = phaseConnections(edges, owner);

    const downstream = reachableFrom(focusOwner, outgoing);
    const settled = settleDownstream(
        restored,
        groups,
        downstream,
        incoming,
        direction,
        gap,
    );

    return packParallelPeers(
        settled,
        groups,
        focusOwner,
        downstream,
        direction,
        gap,
    );
}

export function separateRunbookPhaseBranches(nodes, edges, direction = 'TB', gap = PHASE_BRANCH_GAP) {
    const visible = (nodes ?? []).filter((node) => !node.hidden);
    const byId = new Map(visible.map((node) => [node.id, node]));
    const { groups: rawGroups, owner } = phaseGroups(nodes, edges);
    const groups = rawGroups.map((group) => group.ids.length === 1 && group.root.data?.marker
        ? standaloneGroup(group.root)
        : phaseGroup(group.root, group.ids, group.ids.map((id) => byId.get(id))));

    const graph = new dagre.graphlib.Graph();
    graph.setDefaultEdgeLabel(() => ({}));
    graph.setGraph({ rankdir: direction, nodesep: gap, ranksep: gap, marginx: 0, marginy: 0 });
    for (const group of groups) {
        graph.setNode(group.id, { width: group.width, height: group.height });
    }

    const mappedEdges = (edges ?? []).filter((edge) => !edge.hidden)
        .map((edge) => ({
            ...edge,
            source: owner.get(edge.source),
            target: owner.get(edge.target),
        }))
        .filter((edge) => edge.source && edge.target && edge.source !== edge.target);
    for (const edge of planLayout(mappedEdges).ranked) {
        graph.setEdge(edge.source, edge.target, { weight: edge.weight });
    }
    dagre.layout(graph);

    const shifts = new Map();
    for (const group of groups) {
        const laid = graph.node(group.id);
        for (const id of group.ids) {
            shifts.set(id, {
                x: laid.x - group.anchor.x,
                y: laid.y - group.anchor.y,
            });
        }
    }

    return (nodes ?? []).map((node) => {
        const shift = shifts.get(node.id);
        if (!shift) return node;

        return {
            ...node,
            position: {
                x: node.position.x + shift.x,
                y: node.position.y + shift.y,
            },
        };
    });
}
